import {Modal} from '@/components/atoms'
import {CustomDataTable} from '@/components/organisms'
import {
  formatErrorRemark,
  getRowErrorCount,
  getSubRowParentId,
  useUploadHistoryMainRows,
  useUploadHistorySubRows,
} from '@/hooks/useUploadHistoryTableModal'
import {MODAL_TYPE} from '@/utils/constants/globalStyle'
import {useMemo} from 'react'
import './UploadHistoryTableModal.scss'

/**
 * A reusable UploadHistoryTableModal that accepts:
 * - mainData: an array of row objects for the main table
 * - mainColumns: an array of column definitions for the main table
 * - subData: an array of objects for the sub-rows
 * - subColumns: an array of column definitions for the sub-table
 *
 * The table expands to show subData corresponding to each main row's "id".
 */
const UploadHistoryTableModal = ({
  openUploadHistory,
  setOpenUploadHistory,
  mainData = [],
  mainColumns = [],
  subColumns = [],
  fetchSubRows,
  isDark = true,
  totalRecords = 0,
  setPageIndex = () => {},
  text = '',
}) => {
  const {mainDataSubset, tableHasExpandableRows} = useUploadHistoryMainRows(mainData)

  // Component to render sub rows table
  const SubRowsTable = ({parentId}) => {
    const {rows, isFetching, defaultSubColumns} = useUploadHistorySubRows(parentId, fetchSubRows)
    const displayedSubColumns = subColumns.length > 0 ? subColumns : defaultSubColumns

    const transformedSubColumns = useMemo(
      () =>
        displayedSubColumns.map(col => {
          const isStatusRemark = col.header === 'STATUS (REMARK)'
          const name_of_db_column = isStatusRemark ? 'errorCol-errorMsg' : (col.name_of_db_column ?? col.columnKey)

          return {
            name_of_db_column,
            label_of_column: col.label_of_column ?? col.header,
            filterVariant: col.filterVariant || 'text',
            renderCell: (value, rowData, columnDef) => {
              if (isStatusRemark) {
                return formatErrorRemark(rowData)
              }

              // Use col.cellData if it exists
              if (col.cellData) {
                try {
                  return col.cellData({row: {original: rowData}}, columnDef)
                } catch {
                  return col.cellData(value, rowData, columnDef)
                }
              }

              return value
            },
          }
        }),
      [displayedSubColumns]
    )

    const subComponentProps = {
      id: `rw-upload-history-table-sub-component-${parentId}`,
      records: rows,
      columns: transformedSubColumns,
      isDark,
      pageSize: rows.length || 10,
      totalRecords: rows.length,
      enableSelection: false,
      showResetAll: false,
      showColumnFilters: false,
      enableSorting: false,
      isExpandable: false,
      showActions: false,
      isLoading: isFetching,
      showFooter: false,
    }

    return <CustomDataTable {...subComponentProps} />
  }

  /**
   * Renders the sub-rows for an expanded row.
   * Note: we use `row.original.id` to match `parentId` in subData.
   */
  const renderSubComponent = row => {
    // Only rows with errorCount or failedRecords > 0 should be expandable
    if (!(getRowErrorCount(row?.original) > 0)) return null

    const parentId = getSubRowParentId(row.original)
    if (fetchSubRows) {
      return (
        <div className="sub-component-container">
          <SubRowsTable parentId={parentId} />
        </div>
      )
    }
    return null
  }

  /**
   * Transform mainColumns to the prop structure expected by CustomDataTable
   * CustomDataTable expects `name_of_db_column` & `label_of_column` keys.
   */
  const transformedColumns = useMemo(
    () =>
      mainColumns.map(col => ({
        name_of_db_column: col.name_of_db_column ?? col.columnKey,
        label_of_column: col.label_of_column ?? col.header, //
        filterVariant: col.filterVariant || 'text',
        ...(col.cellData && {
          renderCell: (value, rowData, columnDef) => {
            try {
              return col.cellData({row: {original: rowData}}, columnDef)
            } catch (e) {
              return col.cellData(value, rowData, columnDef)
            }
          },
        }),
        ...(col.isClickable && {
          isClickable: true,
          onCellClick: col.onCellClick,
        }),
      })),
    [mainColumns]
  )

  /**
   * Default props for the main DataTable
   */
  const defaultProps = {
    id: 'rw-upload-history-table',
    records: mainDataSubset,
    columns: transformedColumns,
    isDark,
    pageSize: 10,
    totalRecords: totalRecords,
    setPageIndex,
    enableSelection: false,
    showResetAll: false,
    showColumnFilters: false,
    enableSorting: false,
    isExpandable: tableHasExpandableRows,
    renderSubComponent,
    showFilterButton: false,
    showFilterToolbar: false,
    rowSelection: {},
    onGlobalSearchChange: undefined,
    selection: null,
    showActions: false,
    showFooter: false,
  }

  return (
    <Modal
      open={openUploadHistory}
      onCloseModal={() => setOpenUploadHistory(false)}
      title="Upload History"
      showModal={openUploadHistory}
      isDark={isDark}
      type={MODAL_TYPE.LARGE}
      text={text}
      content={
        <div className="rw-upload-history-modal-content">
          <CustomDataTable {...defaultProps} />
        </div>
      }
    />
  )
}

export default UploadHistoryTableModal
